'use client'

import React, { useState } from 'react'
import { Product } from '@/payload-types'
import { FeaturedProductCard } from './FeaturedProductCard'
import { SectionHeader } from '@/components/ui/SectionHeader'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel'
import { LiquidMetalButton } from '@/components/ui/liquid-metal-button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import Autoplay from 'embla-carousel-autoplay'
import { useTranslations } from 'next-intl'
import styles from './featured.module.css'

interface FeaturedProductsProps {
  products: Product[]
  title?: string
}

export const FeaturedProducts = ({ products, title }: FeaturedProductsProps) => {
  const t = useTranslations('product')
  const commonT = useTranslations('common')
  const [api, setApi] = useState<CarouselApi>()
  if (!products || products.length === 0) return null

  return (
    <div className="w-full max-w-[80rem] mx-auto my-3">
      {/* Experimental (FeaturedProducts only): peel the glass layers away — the
          header bar and the section container are gone, so the heading, CTA and
          cards sit straight on the lava background. Cards use their own
          FeaturedProductCard (blur-less glass, no cart pill). */}
      <SectionHeader
        title={title || t('bestSellers')}
        href="/shop"
        buttonLabel={commonT('viewAll')}
        testId="featured-title"
        bare
      />
      <section
        data-testid="featured-products"
        className="w-full px-1 sm:px-2 lg:px-3 py-2 sm:py-3 lg:py-4"
      >
        <Carousel
          opts={{ align: 'start', loop: true }}
          plugins={[Autoplay({ delay: 4000, stopOnInteraction: true })]}
          setApi={setApi}
          className="relative w-full"
        >
          {/* Side gutters so the circular arrows don't overlap the edge cards */}
          <div className="px-4 md:px-16 lg:px-20">
            <CarouselContent
              data-testid="products-grid"
              className="-ml-4"
              viewportClassName={styles.fadeEdges}
            >
              {products.map((product, index) => (
                <CarouselItem
                  key={product.id}
                  data-testid="product-item"
                  className="pl-4 md:basis-1/2 lg:basis-1/3"
                >
                  <div className="h-full">
                    <FeaturedProductCard product={product} priority={index < 3} />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </div>

          {/* Circular liquid-metal arrow buttons (metal-lab .lmb--icon), theme-aware,
              arrow-only. Desktop only — mobile scrolls by drag. */}
          <div className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 z-30 hidden md:block">
            <LiquidMetalButton
              viewMode="icon"
              appearance="auto"
              icon={<ChevronLeft className="w-5 h-5" />}
              onClick={() => api?.scrollPrev()}
            />
          </div>
          <div className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 z-30 hidden md:block">
            <LiquidMetalButton
              viewMode="icon"
              appearance="auto"
              icon={<ChevronRight className="w-5 h-5" />}
              onClick={() => api?.scrollNext()}
            />
          </div>
        </Carousel>
      </section>
    </div>
  )
}
